import type { PaymentResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { DetailCard } from '@trellis/roots/data'
import { ConfirmDialog } from '@trellis/roots/overlays'
import { Typography } from '@trellis/roots/typography'
import { FEATURE } from '@trellis/shared'
import {
  Check,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  useDeletePaymentMutation,
  useDisclosure,
  useGetPaymentUrlMutation,
  useInvoicePayments,
  useStripeConnection,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

import { PaymentDetailSheet } from './PaymentDetailSheet'
import { RecordPaymentDialog } from './RecordPaymentDialog'

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  STRIPE_CARD: 'Card',
  STRIPE_OTHER: 'Stripe',
  MANUAL: 'Manual',
}

interface InvoicePaymentSectionProps {
  invoiceId: string
  status: string
  currency: string
  total: number
}

function TotalPaid({ amountPaid, currency }: { amountPaid: number; currency: string }) {
  return (
    <span className="text-sm">
      <span className="font-semibold">Total paid:</span>{' '}
      {formatCurrency(amountPaid, currency)}
    </span>
  )
}

function RemovePaymentDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPending: boolean
}) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Remove this payment?"
      description="This deletes the payment record and its receipt, and returns the invoice to Sent/Overdue. This changes the invoice's audit trail and cannot be undone."
      confirmLabel="Remove payment"
      isPending={isPending}
      onConfirm={onConfirm}
    />
  )
}

function PaymentRow({
  payment,
  currency,
  isPaid,
  onPreview,
}: {
  payment: PaymentResponseDto
  currency: string
  isPaid: boolean
  onPreview: (payment: PaymentResponseDto) => void
}) {
  return (
    <tr className="border-t">
      <td className="py-2.5 pr-2 pl-4 align-top whitespace-nowrap">
        <div className="text-muted-foreground">
          {formatDate(payment.paidAt, { dateStyle: 'medium' })}
        </div>
        <div className="text-muted-foreground/70 mt-0.5 text-xs">
          {formatDate(payment.paidAt, { timeStyle: 'short' })}
        </div>
      </td>
      <td className="text-muted-foreground py-2.5 pr-2 align-top whitespace-nowrap">
        {PAYMENT_METHOD_LABELS[payment.method] ?? payment.method}
      </td>
      <td
        className={cn(
          'py-2.5 pr-2 text-right align-top font-medium whitespace-nowrap tabular-nums',
          isPaid && 'text-green-700',
        )}
      >
        {formatCurrency(Number(payment.amount), currency)}
      </td>
      <td className="py-2.5 pr-3 text-right align-top">
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => onPreview(payment)}
          aria-label="Preview payment"
        >
          <Eye className="size-4" />
        </Button>
      </td>
    </tr>
  )
}

function PaymentsTable({
  payments,
  currency,
  isPaid,
  onPreview,
}: {
  payments: PaymentResponseDto[]
  currency: string
  isPaid: boolean
  onPreview: (payment: PaymentResponseDto) => void
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-muted-foreground text-xs tracking-wide uppercase">
          <th className="py-2 pr-2 pl-4 text-left font-medium">Date</th>
          <th className="py-2 pr-2 text-left font-medium">Method</th>
          <th className="py-2 pr-2 text-right font-medium">Amount</th>
          <th className="py-2 pr-4" />
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => (
          <PaymentRow
            key={payment.id}
            payment={payment}
            currency={currency}
            isPaid={isPaid}
            onPreview={onPreview}
          />
        ))}
      </tbody>
    </table>
  )
}

export function InvoicePaymentSection({
  invoiceId,
  status,
  currency,
  total,
}: InvoicePaymentSectionProps) {
  const { isEnabled: isStripeEnabled } = useIsFeatureEnabled(FEATURE.STRIPE_PAYMENTS)
  const { data: connectionResult } = useStripeConnection(isStripeEnabled)
  const { data: paymentsResult } = useInvoicePayments(invoiceId)
  const getPaymentUrl = useGetPaymentUrlMutation()
  const deletePaymentMutation = useDeletePaymentMutation(invoiceId)
  const [copied, setCopied] = useState(false)
  const recordPaymentDialog = useDisclosure()
  const paymentPreview = useDisclosure<PaymentResponseDto>()
  const removePayment = useDisclosure<string>()
  const [prevStatus, setPrevStatus] = useState(status)

  /*
   * A mutation's own onSuccess (e.g. RecordPaymentDialog closing itself) can be skipped by
   * TanStack Query if invalidating queries flips `status` — unmounting the branch that
   * rendered the dialog — before the mutation settles: MutationObserver#notify() drops the
   * mutate-call-level callbacks once its component has unmounted. That leaves
   * recordPaymentDialog/paymentPreview/removePayment stuck, and they resurface as an
   * unexpectedly-open dialog once a later status change remounts that branch. Resetting
   * them here whenever status changes closes that gap regardless of which mutation caused it.
   */
  if (status !== prevStatus) {
    setPrevStatus(status)
    recordPaymentDialog.close()
    paymentPreview.close()
    removePayment.close()
  }

  const isConnectionActive =
    isStripeEnabled && connectionResult?.data?.status === 'ACTIVE'
  const isUnpaidSentOrOverdue = status === 'SENT' || status === 'OVERDUE'
  const shouldFetchPaymentUrl = isConnectionActive && isUnpaidSentOrOverdue

  useEffect(() => {
    let isCancelled = false
    if (shouldFetchPaymentUrl && !isCancelled) {
      getPaymentUrl.mutate(invoiceId)
    }
    return () => {
      isCancelled = true
    }
    /* getPaymentUrl.mutate is stable per hook instance; including it would re-trigger
       the fetch on every render since useMutation returns a new object each render. */
  }, [invoiceId, shouldFetchPaymentUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'DRAFT') return null

  const payments = paymentsResult?.data ?? []
  const succeededPayments = payments.filter((payment) => payment.status === 'SUCCEEDED')
  const manualReconciliationPayment = payments.find(
    (payment) => payment.requiresManualReconciliation,
  )
  const amountPaid = succeededPayments.reduce(
    (sum, payment) => sum + Number(payment.amount),
    0,
  )
  const remainingBalance = total - amountPaid

  function handleCopy(url: string) {
    void navigator.clipboard.writeText(url)
    setCopied(true)
    toast.success('Payment link copied')
    setTimeout(() => setCopied(false), 2000)
  }

  function handleRegenerate() {
    getPaymentUrl.mutate(invoiceId, {
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to refresh payment link'
        toast.error('Failed to refresh payment link', { description: message })
      },
    })
  }

  function handleRemoveFromPreview(paymentId: string) {
    paymentPreview.close()
    removePayment.open(paymentId)
  }

  function handleRemovePayment() {
    if (!removePayment.value) return
    deletePaymentMutation.mutate(removePayment.value, {
      onSuccess: () => {
        toast.success('Payment removed')
        removePayment.close()
      },
      onError: (removeError) => {
        toast.error('Failed to remove payment', { description: removeError.message })
      },
    })
  }

  if (status === 'PAID') {
    if (succeededPayments.length === 0) return null
    return (
      <>
        <DetailCard
          title="Payment"
          icon={CreditCard}
          contentClassName="p-0"
          footer={<TotalPaid amountPaid={amountPaid} currency={currency} />}
        >
          <PaymentsTable
            payments={succeededPayments}
            currency={currency}
            isPaid
            onPreview={paymentPreview.open}
          />
        </DetailCard>
        <PaymentDetailSheet
          invoiceId={invoiceId}
          payment={paymentPreview.value}
          currency={currency}
          onOpenChange={paymentPreview.onOpenChange}
          onRemove={handleRemoveFromPreview}
        />
        <RemovePaymentDialog
          open={succeededPayments.some((payment) => payment.id === removePayment.value)}
          onOpenChange={removePayment.onOpenChange}
          onConfirm={handleRemovePayment}
          isPending={deletePaymentMutation.isPending}
        />
      </>
    )
  }

  if ((status === 'CANCELLED' || status === 'VOID') && manualReconciliationPayment) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
        <p className="font-medium text-amber-900">
          Payment requires manual reconciliation
        </p>
        <p className="mt-1 text-amber-700">
          A payment of{' '}
          {formatCurrency(Number(manualReconciliationPayment.amount), currency)} was
          received after this invoice was {status.toLowerCase()}. Reconcile this payment
          manually.
        </p>
      </div>
    )
  }

  if (!isUnpaidSentOrOverdue) return null

  const url = getPaymentUrl.data?.data.url ?? null
  const isLoadingUrl = getPaymentUrl.isPending && !url
  /*
   * Live coverage check, not payments.find(p => p.requiresManualReconciliation) — that
   * flag is set once at creation time and never updated, so a payment that completed the
   * total when recorded stays flagged false even after a later removal leaves it short
   * again, making it invisible and unremovable here.
   */
  const hasPartialPayment = amountPaid > 0 && remainingBalance > 0
  const hasPaymentLinkContent = isConnectionActive
  const hasCardBody = hasPartialPayment || hasPaymentLinkContent

  return (
    <div className="space-y-4">
      <DetailCard
        title="Payment"
        icon={CreditCard}
        headerAction={
          <Button size="sm" onClick={() => recordPaymentDialog.open(true)}>
            <Plus className="size-3.5" />
            Record Payment
          </Button>
        }
        contentClassName="p-0"
        footer={
          hasPartialPayment ? (
            <TotalPaid amountPaid={amountPaid} currency={currency} />
          ) : undefined
        }
      >
        {hasCardBody && (
          <>
            {hasPartialPayment && (
              <PaymentsTable
                payments={succeededPayments}
                currency={currency}
                isPaid={false}
                onPreview={paymentPreview.open}
              />
            )}
            {hasPaymentLinkContent && (
              <div className={cn('p-4', hasPartialPayment && 'border-t')}>
                {isLoadingUrl ? (
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 className="size-4 animate-spin" />
                    Generating payment link…
                  </div>
                ) : url ? (
                  <>
                    <div className="flex items-center justify-between">
                      <Typography variant="label" color="muted">
                        Payment link
                      </Typography>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRegenerate}
                        disabled={getPaymentUrl.isPending}
                        aria-label="Regenerate payment link"
                      >
                        <RefreshCw
                          className={getPaymentUrl.isPending ? 'animate-spin' : ''}
                        />
                      </Button>
                    </div>
                    <p className="text-muted-foreground truncate font-mono text-xs">
                      {url}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleCopy(url)}>
                        {copied ? (
                          <Check className="size-3.5" />
                        ) : (
                          <Copy className="size-3.5" />
                        )}
                        Copy link
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={url} target="_blank" rel="noreferrer">
                          <ExternalLink className="size-3.5" />
                          Open
                        </a>
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No payment link available yet.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </DetailCard>

      <RecordPaymentDialog
        invoiceId={invoiceId}
        currency={currency}
        outstandingAmount={remainingBalance}
        open={recordPaymentDialog.isOpen}
        onOpenChange={recordPaymentDialog.onOpenChange}
      />
      {hasPartialPayment && (
        <>
          <PaymentDetailSheet
            invoiceId={invoiceId}
            payment={paymentPreview.value}
            currency={currency}
            onOpenChange={paymentPreview.onOpenChange}
            onRemove={handleRemoveFromPreview}
          />
          <RemovePaymentDialog
            open={succeededPayments.some((payment) => payment.id === removePayment.value)}
            onOpenChange={removePayment.onOpenChange}
            onConfirm={handleRemovePayment}
            isPending={deletePaymentMutation.isPending}
          />
        </>
      )}
    </div>
  )
}
